import React from 'react';
import { Alert, FormControl, IconButton, InputAdornment, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Button from '../atoms/Button';
import { LoginService } from '../../services/LoginService';
import styles from '../../pages/login/Login.module.css';

interface PasswordRecoveryProps {
  mode: 'forgot' | 'recover';
  recoveryToken: string;
  onBackToLogin: () => void;
  onRecovered: () => void;
}

const PasswordRecovery: React.FC<PasswordRecoveryProps> = ({
  mode,
  recoveryToken,
  onBackToLogin,
  onRecovered,
}) => {
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [forgotMessage, setForgotMessage] = React.useState('');
  const [forgotError, setForgotError] = React.useState('');
  const [forgotLoading, setForgotLoading] = React.useState(false);
  const [recoverPassword, setRecoverPassword] = React.useState('');
  const [recoverConfirmPassword, setRecoverConfirmPassword] = React.useState('');
  const [recoverErrors, setRecoverErrors] = React.useState<Record<string, boolean>>({});
  const [recoverHelperText, setRecoverHelperText] = React.useState('');
  const [recoverConfirmHelperText, setRecoverConfirmHelperText] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [recoverSuccess, setRecoverSuccess] = React.useState('');
  const [recoverError, setRecoverError] = React.useState('');
  const recoverRedirectTimer = React.useRef<number | null>(null);

  const minPasswordLength = 8;
  const passwordHasMinLength = recoverPassword.length >= minPasswordLength;
  const passwordHasLetter = /[A-Za-z]/.test(recoverPassword);
  const passwordHasNumber = /\d/.test(recoverPassword);
  const isPasswordComplex = passwordHasMinLength && passwordHasLetter && passwordHasNumber;
  const isConfirmValid = recoverPassword === recoverConfirmPassword;
  const passwordStatusText =
    recoverPassword.trim().length > 0
      ? isPasswordComplex
        ? 'Senha atende aos requisitos'
        : 'Use no mínimo 8 caracteres com letras e números'
      : '';
  const confirmStatusText =
    recoverConfirmPassword.trim().length > 0
      ? isConfirmValid
        ? 'Senhas conferem'
        : 'As senhas não conferem'
      : '';

  React.useEffect(() => {
    setForgotMessage('');
    setForgotError('');
    setRecoverSuccess('');
    setRecoverError('');
    if (recoverRedirectTimer.current) {
      window.clearTimeout(recoverRedirectTimer.current);
      recoverRedirectTimer.current = null;
    }
  }, [mode]);

  const handleForgotPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setForgotMessage('');
    setForgotError('');

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError('Informe um e-mail válido.');
      return;
    }

    try {
      setForgotLoading(true);
      await LoginService.forgotPassword(forgotEmail);
      setForgotMessage('Enviamos um link de recuperação para o seu e-mail.');
    } catch (err: any) {
      setForgotError(err?.message || 'Erro ao solicitar recuperação.');
    } finally {
      setForgotLoading(false);
    }
  };

  const validateRecover = () => {
    const newErrors: Record<string, boolean> = {};
    setRecoverHelperText('');
    setRecoverConfirmHelperText('');

    if (!recoverPassword.trim()) newErrors.password = true;
    if (!recoverConfirmPassword.trim()) newErrors.confirmPassword = true;

    if (recoverPassword.trim() && !isPasswordComplex) {
      newErrors.password = true;
      setRecoverHelperText('Mínimo 8 caracteres, com letras e números');
    }

    if (recoverPassword && recoverConfirmPassword && recoverPassword !== recoverConfirmPassword) {
      newErrors.confirmPassword = true;
      setRecoverConfirmHelperText('As senhas não conferem');
    }

    setRecoverErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRecoverPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setRecoverSuccess('');
    setRecoverError('');
    if (!validateRecover()) return;

    try {
      await LoginService.recoverPassword(recoverPassword, recoveryToken);
      setRecoverSuccess('Senha alterada com sucesso.');
      setRecoverPassword('');
      setRecoverConfirmPassword('');
      recoverRedirectTimer.current = window.setTimeout(() => {
        onRecovered();
      }, 1800);
    } catch (err: any) {
      console.log(err);
      setRecoverError(err?.data?.detail || 'Erro ao redefinir senha.');
    }
  };

  if (mode === 'forgot') {
    return (
      <>
        <form onSubmit={handleForgotPassword}>
          <div className={styles.formStack}>
            <FormControl fullWidth>
              <TextField
                variant="outlined"
                label="Endereço de e-mail"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={forgotLoading}
              />
            </FormControl>
            {!!forgotError && <Alert severity="error">{forgotError}</Alert>}
            {!!forgotMessage && <Alert severity="success">{forgotMessage}</Alert>}
            <FormControl fullWidth>
              <Button
                type="submit"
                variant="contained"
                label={forgotLoading ? 'Enviando...' : 'Enviar link'}
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                sx={{
                  backgroundColor: '#1f75cf',
                  color: '#ffffff',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(31, 117, 207, 0.2)',
                  '&:hover': {
                    backgroundColor: '#1a67b6',
                  },
                }}
              />
            </FormControl>
          </div>
        </form>
        <div className={styles.supportRow} style={{ marginTop: 20, fontSize: 13 }}>
          <button style={{ fontSize: 15 }} className={styles.linkButton} onClick={onBackToLogin}>
            Voltar ao Login
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {!recoveryToken && (
        <Alert severity="warning">Token de recuperação inválido. Solicite um novo link.</Alert>
      )}
      <form onSubmit={handleRecoverPassword}>
        <div className={styles.formStack}>
          <div className={styles.row}>
            <TextField
              fullWidth
              label="Nova senha"
              type={showPassword ? 'text' : 'password'}
              value={recoverPassword}
              error={recoverErrors.password}
              helperText={recoverHelperText || passwordStatusText}
              variant="outlined"
              onChange={(e) => setRecoverPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmar senha"
              type={showPassword ? 'text' : 'password'}
              value={recoverConfirmPassword}
              error={recoverErrors.confirmPassword}
              helperText={recoverConfirmHelperText || confirmStatusText}
              variant="outlined"
              onChange={(e) => setRecoverConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          {!!recoverError && <Alert severity="error">{recoverError}</Alert>}
          {!!recoverSuccess && <Alert severity="success">{recoverSuccess}</Alert>}
          <FormControl fullWidth>
            <Button
              type="submit"
              variant="contained"
              label="Salvar nova senha"
              onClick={handleRecoverPassword}
              disabled={!recoveryToken}
              sx={{
                backgroundColor: '#1f75cf',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(31, 117, 207, 0.2)',
                '&:hover': {
                  backgroundColor: '#1a67b6',
                },
              }}
            />
          </FormControl>
        </div>
      </form>
      <div className={styles.supportRow} style={{ marginTop: 20, fontSize: 13 }}>
        <button style={{ fontSize: 15 }} className={styles.linkButton} onClick={onBackToLogin}>
          Já possui conta?
        </button>
      </div>
    </>
  );
};

export default PasswordRecovery;
